import moment from 'moment'
import {
  DATETIME_DEFAULT_FORMAT,
  DATE_FILENAME_FORMAT,
  MAX_NUMBER_INTEGER,
  MAX_STRING_LENGTH,
} from '../constant/global/Config'
import { CURRENCY } from '../constant/global/Currency'
import * as Yup from 'yup'

const arraySorter = (array, field, ascending) => {
  let order = ascending === 'asc' ? true : false
  let sorted = []

  if (order) {
    sorted = [].concat(array).sort((a, b) => (a[field] > b[field] ? 1 : -1))
  } else {
    sorted = [].concat(array).sort((a, b) => (a[field] < b[field] ? 1 : -1))
  }

  return sorted
}

const currencyFormat = (value = 0, currency_type = 'IDR') => {
  if (!currency_type) return value
  if (!CURRENCY[currency_type]) return numberFormat(value)
  if (CURRENCY[currency_type].position === 'LEADING')
    return `${CURRENCY[currency_type].abv} ${numberFormat(value)}`
  if (CURRENCY[currency_type].position === 'FOLLOWING')
    return `${numberFormat(value)} ${CURRENCY[currency_type].abv}`
  return value
}

const downloadFile = (blob, fileName) => {
  const link = document.createElement('a')
  // create a blobURI pointing to our Blob
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  // some browser needs the anchor to be in the doc
  document.body.append(link)
  link.click()
  link.remove()
  // in case the Blob uses a lot of memory
  setTimeout(() => URL.revokeObjectURL(link.href), 7000)
}

const findByArray = (array, key, value) => {
  return array.find((element) => {
    return element[key] === value
  })
}

const formatDate = (value, format = DATETIME_DEFAULT_FORMAT) => {
  if (value !== undefined) {
    value = moment(value)
    const unix_timestamp = Date.parse(new Date(value).toString())
    return moment(unix_timestamp).format(format)
  }
  return null
}

const formatMoment = (value) => {
  if (value !== undefined) return moment(value)
  return null
}

export const generateFilename = (name, extension) => {
  const time = moment().format(DATE_FILENAME_FORMAT)
  const str = name.replace(/\s+/g, '-').toUpperCase()
  return `${str}-${time}.${extension}`
}

export const validateWhitespaceInput = (value) => {
  return value.replace(/^\s+|\s+$|\s+(?=\s)/g, '')
}

const numberFormat = (value, options = { leadingZeroAllowed: false }) => {
  if (value === 0 || value === '0') return '0'
  if (!value) return ''
  let parsed = typeof value === 'string' || value instanceof String ? value : value.toString()
  if (!options.leadingZeroAllowed) parsed = parsed.replace(/^[0,]+/g, '')
  let number_string = parsed.replace(/[^,\d]/g, '')
  let split = number_string.split(','),
    sisa = split[0].length % 3,
    rupiah = split[0].substring(0, sisa),
    ribuan = split[0].substring(sisa).match(/\d{3}/gi)

  if (ribuan) {
    let separator = sisa ? '.' : ''
    rupiah += separator + ribuan.join('.')
  }

  rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah
  return rupiah
}

export const decimalFormat = (value) => {
  if (value === undefined || value === null) return ''
  const parsedValue = typeof value === 'string' ? value : value.toString()

  if (parsedValue.charAt(0) === '0' && !parsedValue.startsWith('0,')) {
    if (parsedValue.length === 2 && parsedValue.charAt(1) !== ',') {
      return '0,'
    }
    return parsedValue
  }

  return numberFormat(parsedValue, { leadingZeroAllowed: true })
}

export const formatNumberDecimal = (value, decimalPlaces = 2) => {
  if (value > MAX_NUMBER_INTEGER) {
    value = MAX_NUMBER_INTEGER
  }
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const num = Number(value)
  const roundedNum = parseFloat(num.toFixed(decimalPlaces))
  const [integerPart, decimalPart] = roundedNum.toString().split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  if (decimalPart) {
    return `${formattedInteger},${decimalPart}`
  } else {
    return formattedInteger
  }
}

export const currencyFormatDecimal = (value, currency = 'IDR', decimalPlaces = 2) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const num = Number(value)

  if (isNaN(num)) {
    return ''
  }

  const roundedNum = parseFloat(num.toFixed(decimalPlaces))
  const [integerPart, decimalPart] = roundedNum.toString().split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const prefix = currency === 'IDR' ? 'Rp. ' : currency

  if (decimalPart) {
    return `${prefix}${formattedInteger},${decimalPart}`
  } else {
    return `${prefix}${formattedInteger}`
  }
}

export const formatNumberDecimalDestroy = (value) => {
  if (value === null || value === undefined) return ''
  if (value === '') return 0
  if (typeof value === 'number') return value
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0
}

export const zeroDecimal = (originalValue, amountZeroDecimal) => {
  const value = (+originalValue).toFixed(amountZeroDecimal).replace(/(\.0+|0+)$/, '')
  return value
}

const numberFormatAPI = (
  value,
  options = {
    leadingZeroAllowed: false,
    zeroDecimal: false,
    amountZeroDecimal: 2,
  },
) => {
  if (options.zeroDecimal) value = zeroDecimal(value, options.amountZeroDecimal)
  if (value === 0 || value === '0') return '0'
  if (!value) return ''
  let parsed = typeof value === 'string' || value instanceof String ? value : value.toString()
  if (!options.leadingZeroAllowed) parsed = parsed.replace(/^[0,]+/g, '')
  let number_string = parsed.replace(/[^.\d]/g, '')
  let split = number_string.split('.'),
    sisa = split[0].length % 3,
    rupiah = split[0].substring(0, sisa),
    ribuan = split[0].substring(sisa).match(/\d{3}/gi)

  if (ribuan) {
    let separator = sisa ? '.' : ''
    rupiah += separator + ribuan.join('.')
  }

  rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah
  return rupiah
}

const numberFormatDestroy = (value) => {
  if (!value) return 0
  const parsed = typeof value === 'string' || value instanceof String ? value : value.toString()
  return parseFloat(parsed.replace(/([^,\d]+)/g, '').replace(',', '.'))
}

export const getFormattedAmountAfterTax = (values, decimal = 2) => {
  const parseValue = (val) => {
    if (typeof val === 'string') {
      return formatNumberDecimalDestroy(val)
    }
    return typeof val === 'number' ? val : 0
  }

  const total = parseValue(values?.total_amount)
  const tax = parseValue(values?.tax_pph_amount)

  const result = Math.round(total - tax)

  return formatNumberDecimal(result, decimal)
}

const numberFormatLocal = (value) => {
  if (!value) return ''
  const parsed = value.toString()
  return parsed.replace('.', ',')
}

const replaceLongText = (value = '') => {
  const words = value.replace(/\s+/g, ' ').trim().split(/[\s]/)
  const newWords = []
  words.map((word) => newWords.push(word.length > 100 ? normalizeWord(word) : word))
  return newWords.join(' ')
}

const moneyFormat = (value) => {
  const lookup = [
    { value: 1e9, symbol: ' Miliar' },
    { value: 1e12, symbol: ' Triliun' },
    { value: 1e15, symbol: ' Kuadriliun' },
    { value: 1e18, symbol: ' Kuintiliun' },
  ]

  let item = lookup
    .slice()
    .reverse()
    .find((row) => {
      return value >= row.value
    })

  if (item) {
    const format = parseFloat(value / item.value).toString()
    let plus = ''
    if (format.split('.')[1]) {
      plus = '+'
    }
    return format.split('.')[0] + plus + item.symbol
  }
  return numberFormat(value)
}

const normalizeWord = (value) => {
  if (!value) return ''
  let result = ''
  for (let index = 1; index <= value.length; index++) {
    if (index % 100 === 0) {
      result = value.substring(0, index) + ' ' + value.substring(index + 1)
    }
  }
  return result
}

const subStringContent = (str = '', total = MAX_STRING_LENGTH) => {
  if (str.length > total - 1) return str.slice(0, total) + '...'
  return str
}

const validationHelpersYup = (rules = {}) => {
  let result = Yup

  switch (rules.type?.value) {
    case 'string':
      result = result.string().trim().typeError(rules.type?.message)
      break
    case 'email':
      result = result.string().email(rules.type.message).trim()
      break
    case 'object':
      result = result.object().test('not-empty', rules.required?.message, (value) => {
        if (!rules.required?.value) return true
        return value && Object.keys(value).length > 0
      })
      break
    case 'number':
      result = result.number()
      break
    case 'numberformat':
      result = result.number().transform((_, value) => {
        return numberFormatDestroy(value)
      })
      break
    case 'array':
      result = result.array()
      break
    default:
      break
  }

  if (rules.min_length) result = result.min(rules.min_length.value, rules.min_length.message)
  if (rules.max_length) result = result.max(rules.max_length.value, rules.max_length.message)
  if (rules.required?.value) result = result.required(rules.required.message)
  if (rules.nullable?.value) result = result.nullable()

  return result
}

const validNumber = (evt) => {
  let pattern = /[0-9]/
  if (!pattern.test(evt.key)) evt.preventDefault()
}

const validUsername = (evt) => {
  let pattern = /[a-z0-9._]/
  if (!pattern.test(evt.key)) evt.preventDefault()
}

const validNumberFormat = (evt, options = { decimal: true }) => {
  let pattern = options?.decimal ? /^[0-9.]+(,\d{0,2})?$/ : /^[0-9.]+$/
  if (pattern.test(evt?.target?.value) || evt?.target?.value?.length === 0) return true
  return false
}

const validLength = (evt, length = 0) => {
  if (evt?.terget?.value?.length === 0) return false
  if (evt?.terget?.value?.length > length) return false
  return true
}

const FindFileType = (value) => {
  return value.substring(value.lastIndexOf('.') + 1)
}

const stringToJson = (str) => {
  let rows = null
  if (str !== '' || str !== null) {
    rows = JSON.parse(str)
  }
  return rows
}

const trimDecimal = (num, decimalPlaces = 2) => {
  const decimalPart = num % 1 !== 0 ? num.toString().split('.')[1] : null
  if (decimalPart && decimalPart.length > decimalPlaces) {
    return parseFloat(num.toFixed(decimalPlaces))
  }
  return num
}

export {
  arraySorter,
  currencyFormat,
  downloadFile,
  findByArray,
  formatDate,
  formatMoment,
  moneyFormat,
  numberFormat,
  numberFormatAPI,
  numberFormatDestroy,
  numberFormatLocal,
  replaceLongText,
  subStringContent,
  validationHelpersYup,
  validLength,
  validNumber,
  validNumberFormat,
  validUsername,
  FindFileType,
  stringToJson,
  trimDecimal,
}
